import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaGraduationCap, FaFilePdf, FaWhatsapp, FaShareAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { SITE_CONFIG } from '../config/siteConfig';
import { useChoiceList } from '../context/ChoiceListContext';
// jsPDF loaded lazily to keep initial bundle small
const loadJsPDF = async () => {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  return { jsPDF, autoTable };
};

const ChoiceList = () => {
  const { choiceList, removeFromChoiceList, clearChoiceList } = useChoiceList();
  const navigate = useNavigate();

  const shortName = (name: string) => {
    // Preserve Anna University campus identifier (CEG/MIT/ACT)
    const campusMatch = name.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
    if (campusMatch) {
      const base = campusMatch[1].split(',')[0].trim();
      return `${base} — ${campusMatch[2]}`;
    }
    return name.length > 55 && name.includes(',') ? name.substring(0, name.indexOf(',')) : name;
  };

  const handleCompare = () => {
    if (choiceList.length < 2) return;
    const ids = [...new Set(choiceList.map(c => c.id))].join(',');
    navigate(`/cutoffs?colleges=${ids}&community=OC`);
  };

  const generatePDF = async () => {
    const { jsPDF, autoTable } = await loadJsPDF();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const pageWidth = doc.internal.pageSize.width; // 297mm landscape
    const siteUrl = 'https://choosemycollege.in';

    // Header background
    doc.setFillColor(37, 99, 235); // blue-600
    doc.rect(0, 0, pageWidth, 26, 'F');

    // Brand name — centered, large
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const brandText = 'ChooseMyCollege.in';
    const brandWidth = doc.getTextWidth(brandText);
    const brandX = (pageWidth - brandWidth) / 2;
    doc.text(brandText, brandX, 11);

    // Clickable hyperlink over the brand text
    doc.link(brandX, 4, brandWidth, 9, { url: siteUrl });

    // Subtitle — centered
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    const subText = 'My TNEA ChoiceList — choosemycollege.in';
    const subWidth = doc.getTextWidth(subText);
    doc.text(subText, (pageWidth - subWidth) / 2, 20);

    // Date — right aligned
    doc.setFontSize(7);
    const dateText = `Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    doc.text(dateText, pageWidth - 14, 20, { align: 'right' });

    // Table
    autoTable(doc, {
      startY: 32,
      head: [['Pref.', 'Code', 'College Name', 'Br.', 'Branch Name', 'District', 'Type', 'NAAC']],
      body: choiceList.map((item, idx) => [
        idx + 1,
        item.college_code,
        shortName(item.college_name),
        item.branch_code,
        item.branch_name,
        item.district?.name || '—',
        item.college_type || '—',
        item.naac_grade || '—',
      ]),
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 18, fontStyle: 'bold' },
        2: { cellWidth: 70 },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 55 },
        5: { cellWidth: 30 },
        6: { cellWidth: 28 },
        7: { cellWidth: 14, halign: 'center' },
      },
    });

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(7);
    doc.text('choosemycollege.in — TNEA College Predictor & ChoiceList', 14, pageHeight - 6);

    return doc;
  };

  const handleDownload = async () => {
    const doc = await generatePDF();
    doc.save('TNEA_ChoiceList.pdf');
  };

  const handleWhatsApp = async () => {
    const doc = await generatePDF();
    const blob = doc.output('blob');
    const file = new File([blob], 'TNEA_ChoiceList.pdf', { type: 'application/pdf' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'My TNEA ChoiceList',
          text: 'My TNEA college preferences from choosemycollege.in',
        });
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          // User cancelled or error — fallback to download
          doc.save('TNEA_ChoiceList.pdf');
        }
      }
    } else {
      // Desktop fallback — download PDF
      doc.save('TNEA_ChoiceList.pdf');
    }
  };

  return (
    <>
      <Helmet>
        <title>My ChoiceList — College + Branch Preferences | ChooseMyCollege</title>
        <meta name="description" content="Your saved TNEA college and branch preferences. Review and compare before counselling." />
      </Helmet>

      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-2xl p-5 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaGraduationCap className="text-2xl" />
              <div>
                <h1 className="text-xl font-bold">My ChoiceList</h1>
                <p className="text-blue-200 text-sm">College + Branch preferences for TNEA counselling</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{choiceList.length}</div>
              <div className="text-blue-200 text-xs">choices saved</div>
            </div>
          </div>
        </div>

        {choiceList.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <FaGraduationCap className="text-gray-200 text-5xl mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-500 mb-2">Your ChoiceList is empty</h2>
            <p className="text-sm text-gray-400 mb-6">
              Add college + branch choices from the Predictor or College detail pages
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/predictor" className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                Use Predictor
              </Link>
              <Link to="/colleges" className="px-4 py-2 bg-white border border-blue-200 text-blue-600 text-sm font-semibold rounded-lg hover:bg-blue-50 transition-colors">
                Browse Colleges
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* TNEA-style table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-blue-600 text-white text-sm">
                      <th className="text-center px-4 py-3 font-semibold w-12">Pref.</th>
                      <th className="text-left px-4 py-3 font-semibold w-28">Code</th>
                      <th className="text-left px-4 py-3 font-semibold">College Name</th>
                      <th className="text-left px-4 py-3 font-semibold w-16">Br.</th>
                      <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Branch Name</th>
                      <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">District</th>
                      <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Type</th>
                      <th className="text-center px-4 py-3 font-semibold w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {choiceList.map((item, idx) => (
                      <tr key={`${item.college_code}-${item.branch_code}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                        <td className="px-4 py-3 text-center">
                          <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold inline-flex items-center justify-center">
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/colleges/${item.college_code}`} className="font-mono text-xs font-bold text-blue-700 hover:underline">
                            {item.college_code}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/colleges/${item.college_code}`} className="text-sm font-semibold text-gray-800 hover:text-blue-700 hover:underline leading-snug">
                            {shortName(item.college_name)}
                          </Link>
                          {item.naac_grade && (
                            <span className="ml-2 text-[10px] font-semibold text-purple-600">NAAC {item.naac_grade}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                            {item.branch_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{item.branch_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">{item.district?.name || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">{item.college_type}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => removeFromChoiceList(item.college_code, item.branch_code)}
                            className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                            title="Remove"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={clearChoiceList}
                className="text-sm text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear all
              </button>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors"
                >
                  <FaFilePdf />
                  Download PDF
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white text-sm font-semibold rounded-xl hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp />
                  Share on WhatsApp
                </button>
                {choiceList.length >= 2 && (
                  <button
                    onClick={handleCompare}
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    Compare Colleges ({[...new Set(choiceList.map(c => c.id))].length})
                  </button>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center mt-6">
              Saved in your browser · Clears if browser data is cleared · Use the Predictor to find the best options
            </p>

            {/* Share nudge — shown after user has built a list */}
            <div className="mt-5 flex items-center justify-between gap-4 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <p className="text-sm text-blue-700 leading-snug">
                Know a friend preparing for TNEA? Share this free tool 👇
              </p>
              <button
                onClick={async () => {
                  const data = { title: 'ChooseMyCollege — Free TNEA Guidance', text: `Free TNEA ${SITE_CONFIG.year} college predictor & counselling tool!`, url: 'https://choosemycollege.in' };
                  if (navigator.share) {
                    try { await navigator.share(data); } catch { /* cancelled */ }
                  } else {
                    await navigator.clipboard.writeText(data.url);
                    toast.success('Link copied!', { autoClose: 2000 });
                  }
                }}
                className="flex-shrink-0 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
              >
                <FaShareAlt className="text-xs" />
                Share
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ChoiceList;
